import {
  materialTestKey,
  type MaterialsInvestigationConfig,
  type ObservableMaterialProperty,
} from './MaterialsInvestigationConfig.js';

export type MaterialsInvestigationPhase =
  | 'inspect-materials'
  | 'predict'
  | 'test-properties'
  | 'compare-evidence'
  | 'choose-material'
  | 'build-repair'
  | 'complete';

export interface MaterialObservationRecord {
  readonly key: string;
  readonly materialId: string;
  readonly testId: string;
  readonly evidenceId: string;
  readonly propertyId: ObservableMaterialProperty;
  readonly observation: string;
}

export interface MaterialChoiceResult {
  readonly selectedMaterialId: string;
  readonly suitedToPurpose: boolean;
  readonly evidenceIds: readonly string[];
  readonly feedback: string;
}

export interface MaterialsInvestigationSnapshot {
  readonly investigationId: string;
  readonly phase: MaterialsInvestigationPhase;
  readonly inspectedMaterialIds: readonly string[];
  readonly predictedMaterialId?: string;
  readonly completedTestKeys: readonly string[];
  readonly observations: readonly MaterialObservationRecord[];
  readonly pendingObservation?: MaterialObservationRecord;
  readonly selectedMaterialId?: string;
  readonly evidenceSupportingChoiceIds: readonly string[];
  readonly choice?: MaterialChoiceResult;
  readonly hintStage: number;
}

const PHASES: readonly MaterialsInvestigationPhase[] = [
  'inspect-materials',
  'predict',
  'test-properties',
  'compare-evidence',
  'choose-material',
  'build-repair',
  'complete',
];

export class MaterialsInvestigationState {
  private phaseValue: MaterialsInvestigationPhase = 'inspect-materials';
  private readonly inspectedMaterialIds = new Set<string>();
  private predictedMaterialIdValue?: string;
  private readonly observations = new Map<string, MaterialObservationRecord>();
  private pendingObservationValue?: MaterialObservationRecord;
  private selectedMaterialIdValue?: string;
  private evidenceSupportingChoiceIdsValue: string[] = [];
  private choiceValue?: MaterialChoiceResult;
  private hintStageValue = 0;

  constructor(
    readonly config: MaterialsInvestigationConfig,
    initialSnapshot?: MaterialsInvestigationSnapshot,
  ) {
    if (initialSnapshot) this.restore(initialSnapshot);
  }

  get phase() { return this.phaseValue; }
  get isComplete() { return this.phaseValue === 'complete'; }
  get readyToPredict() {
    return this.inspectedMaterialIds.size >= this.config.minimumMaterialsInspected;
  }
  get readyToCompare() {
    return this.pendingObservationValue === undefined
      && this.config.task.requiredObservationKeys.every(key => this.observations.has(key));
  }

  snapshot(): MaterialsInvestigationSnapshot {
    return {
      investigationId: this.config.id,
      phase: this.phaseValue,
      inspectedMaterialIds: [...this.inspectedMaterialIds],
      predictedMaterialId: this.predictedMaterialIdValue,
      completedTestKeys: [...this.observations.keys()],
      observations: [...this.observations.values()],
      pendingObservation: this.pendingObservationValue,
      selectedMaterialId: this.selectedMaterialIdValue,
      evidenceSupportingChoiceIds: [...this.evidenceSupportingChoiceIdsValue],
      choice: this.choiceValue,
      hintStage: this.hintStageValue,
    };
  }

  inspectMaterial(materialId: string) {
    if (this.phaseValue !== 'inspect-materials' || !this.hasMaterial(materialId)) return false;
    if (this.inspectedMaterialIds.has(materialId)) return false;
    this.inspectedMaterialIds.add(materialId);
    return true;
  }

  beginPrediction() {
    if (this.phaseValue !== 'inspect-materials' || !this.readyToPredict) return false;
    this.phaseValue = 'predict';
    return true;
  }

  choosePrediction(materialId: string) {
    if (this.phaseValue !== 'predict' || !this.hasMaterial(materialId)) return false;
    this.predictedMaterialIdValue = materialId;
    this.phaseValue = 'test-properties';
    return true;
  }

  runTest(materialId: string, testId: string): MaterialObservationRecord | undefined {
    if (this.phaseValue !== 'test-properties' || this.pendingObservationValue) return undefined;
    const material = this.config.materials.find(candidate => candidate.id === materialId);
    const test = this.config.tests.find(candidate => candidate.id === testId);
    const definition = test?.observations[materialId];
    const key = materialTestKey(materialId, testId);
    if (!material || !test || !definition || this.observations.has(key)) return undefined;
    this.pendingObservationValue = {
      key,
      materialId,
      testId,
      evidenceId: definition.evidenceId,
      propertyId: definition.propertyId,
      observation: definition.observation,
    };
    return this.pendingObservationValue;
  }

  recordObservation(): MaterialObservationRecord | undefined {
    if (this.phaseValue !== 'test-properties' || !this.pendingObservationValue) return undefined;
    const observation = this.pendingObservationValue;
    this.observations.set(observation.key, observation);
    this.pendingObservationValue = undefined;
    return observation;
  }

  beginComparison() {
    if (this.phaseValue !== 'test-properties' || !this.readyToCompare) return false;
    this.phaseValue = 'compare-evidence';
    return true;
  }

  beginMaterialChoice() {
    if (this.phaseValue !== 'compare-evidence') return false;
    this.phaseValue = 'choose-material';
    return true;
  }

  chooseMaterial(materialId: string): MaterialChoiceResult | undefined {
    if (this.phaseValue !== 'choose-material' || !this.hasMaterial(materialId)) return undefined;
    const result = this.createChoiceResult(materialId);
    this.selectedMaterialIdValue = materialId;
    this.evidenceSupportingChoiceIdsValue = [...result.evidenceIds];
    this.choiceValue = result;
    if (result.suitedToPurpose) this.phaseValue = 'build-repair';
    return result;
  }

  applyMaterial() {
    if (this.phaseValue !== 'build-repair' || !this.choiceValue?.suitedToPurpose) return false;
    this.phaseValue = 'complete';
    return true;
  }

  requestHint() {
    if (this.phaseValue === 'complete' || this.config.hints.length === 0) return undefined;
    const hint = this.config.hints[Math.min(this.hintStageValue, this.config.hints.length - 1)];
    this.hintStageValue = Math.min(this.hintStageValue + 1, this.config.hints.length);
    return hint;
  }

  reset() {
    this.phaseValue = 'inspect-materials';
    this.inspectedMaterialIds.clear();
    this.predictedMaterialIdValue = undefined;
    this.observations.clear();
    this.pendingObservationValue = undefined;
    this.selectedMaterialIdValue = undefined;
    this.evidenceSupportingChoiceIdsValue = [];
    this.choiceValue = undefined;
    this.hintStageValue = 0;
  }

  private hasMaterial(materialId: string) {
    return this.config.materials.some(material => material.id === materialId);
  }

  private createChoiceResult(materialId: string): MaterialChoiceResult {
    const evidence = [...this.observations.values()].filter(observation =>
      observation.materialId === materialId
      && this.config.task.requiredProperties.includes(observation.propertyId));
    const observedRequiredProperties = new Set(evidence.map(observation => observation.propertyId));
    const hasRequiredPropertyEvidence = this.config.task.requiredProperties.every(property =>
      observedRequiredProperties.has(property));
    const suitedToPurpose = materialId === this.config.bestMaterialId && hasRequiredPropertyEvidence;
    const configuredFeedback = this.config.choiceFeedback.find(item => item.materialId === materialId);
    return {
      selectedMaterialId: materialId,
      suitedToPurpose,
      evidenceIds: evidence.map(observation => observation.evidenceId),
      feedback: suitedToPurpose
        ? this.config.successMessage
        : configuredFeedback?.message
          ?? 'Use the test evidence to compare this material with the others, then revise your choice.',
    };
  }

  private canonicalObservation(record: MaterialObservationRecord) {
    const test = this.config.tests.find(candidate => candidate.id === record.testId);
    const definition = test?.observations[record.materialId];
    const key = materialTestKey(record.materialId, record.testId);
    if (!definition || key !== record.key) return undefined;
    return {
      key,
      materialId: record.materialId,
      testId: record.testId,
      evidenceId: definition.evidenceId,
      propertyId: definition.propertyId,
      observation: definition.observation,
    } satisfies MaterialObservationRecord;
  }

  private restore(snapshot: MaterialsInvestigationSnapshot) {
    if (snapshot.investigationId !== this.config.id || !PHASES.includes(snapshot.phase)) return;
    snapshot.inspectedMaterialIds.forEach(id => {
      if (this.hasMaterial(id)) this.inspectedMaterialIds.add(id);
    });
    if (snapshot.predictedMaterialId && this.hasMaterial(snapshot.predictedMaterialId)) {
      this.predictedMaterialIdValue = snapshot.predictedMaterialId;
    }
    snapshot.observations.forEach(record => {
      const canonical = this.canonicalObservation(record);
      if (canonical && !this.observations.has(canonical.key)) {
        this.observations.set(canonical.key, canonical);
      }
    });
    if (snapshot.pendingObservation) {
      const pending = this.canonicalObservation(snapshot.pendingObservation);
      if (pending && !this.observations.has(pending.key)) this.pendingObservationValue = pending;
    }
    this.hintStageValue = Math.max(0, Math.min(
      Number.isFinite(snapshot.hintStage) ? Math.floor(snapshot.hintStage) : 0,
      this.config.hints.length,
    ));

    const phaseNeedsInspection = snapshot.phase !== 'inspect-materials';
    const phaseNeedsPrediction = !['inspect-materials', 'predict'].includes(snapshot.phase);
    const phaseNeedsEvidence = [
      'compare-evidence',
      'choose-material',
      'build-repair',
      'complete',
    ].includes(snapshot.phase);
    if (phaseNeedsInspection && !this.readyToPredict) return this.reset();
    if (phaseNeedsPrediction && !this.predictedMaterialIdValue) return this.reset();
    if (phaseNeedsEvidence && !this.readyToCompare) return this.reset();

    if (snapshot.selectedMaterialId && this.hasMaterial(snapshot.selectedMaterialId)) {
      this.selectedMaterialIdValue = snapshot.selectedMaterialId;
      this.choiceValue = this.createChoiceResult(snapshot.selectedMaterialId);
      this.evidenceSupportingChoiceIdsValue = [...this.choiceValue.evidenceIds];
    }
    const phaseNeedsGoodChoice = ['build-repair', 'complete'].includes(snapshot.phase);
    if (phaseNeedsGoodChoice && !this.choiceValue?.suitedToPurpose) return this.reset();
    this.phaseValue = snapshot.phase;
  }
}
